import React, { useState, useEffect } from 'react';
import { RefreshCw, CheckCircle, XCircle, Clock, AlertTriangle } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface QueueItem {
  id: string;
  event_type: string;
  status: string;
  payload: any;
  response_payload: any;
  tries: number;
  max_retries: number;
  last_error: string | null;
  created_at: string;
  processed_at: string | null;
}

export function SocialQueue() {
  const [queueItems, setQueueItems] = useState<QueueItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [eventTypeFilter, setEventTypeFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  useEffect(() => {
    loadQueue();
    const interval = setInterval(loadQueue, 5000);
    return () => clearInterval(interval);
  }, [eventTypeFilter, statusFilter]);

  const loadQueue = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // First get user's accounts
      const { data: accounts } = await supabase
        .from('social_accounts')
        .select('id')
        .eq('owner_user_id', user.id);

      if (!accounts || accounts.length === 0) {
        setQueueItems([]);
        return;
      }

      const accountIds = accounts.map(a => a.id);

      let query = supabase
        .from('outbound_queue')
        .select('*')
        .in('account_id', accountIds)
        .order('created_at', { ascending: false })
        .limit(50);

      if (eventTypeFilter !== 'all') {
        query = query.eq('event_type', eventTypeFilter);
      }

      if (statusFilter !== 'all') {
        query = query.eq('status', statusFilter.toUpperCase());
      }

      const { data, error } = await query;

      if (error) throw error;
      setQueueItems(data || []);
    } catch (error) {
      console.error('Error loading queue:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'QUEUED':
      case 'PROCESSING':
        return <Clock className="w-5 h-5 text-yellow-500" />;
      case 'SENT':
      case 'ACKED':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'FAILED':
        return <AlertTriangle className="w-5 h-5 text-orange-500" />;
      case 'DLQ':
        return <XCircle className="w-5 h-5 text-red-500" />;
      default:
        return null;
    }
  };

  const getStatusText = (status: string) => {
    const statusMap: Record<string, string> = {
      'QUEUED': 'W kolejce',
      'PROCESSING': 'Przetwarzanie',
      'SENT': 'Wysłane',
      'ACKED': 'Potwierdzone',
      'FAILED': 'Niepowodzenie',
      'DLQ': 'Dead Letter Queue',
    };
    return statusMap[status] || status;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-[#7B4BFF]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-white">Kolejka i logi</h2>
          <p className="text-gray-400 mt-1">
            Podgląd przetwarzanych eventów i ich statusów
          </p>
        </div>
        <button
          onClick={loadQueue}
          className="flex items-center space-x-2 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
        >
          <RefreshCw className="w-5 h-5" />
          <span>Odśwież</span>
        </button>
      </div>

      <div className="space-y-4">
        <div>
          <div className="text-sm text-gray-400 mb-2">Typ wydarzenia:</div>
          <div className="flex space-x-2">
            {[
              { value: 'all', label: 'Wszystkie' },
              { value: 'comment.inbound', label: 'Komentarze' },
              { value: 'dm.inbound', label: 'Wiadomości' },
            ].map((type) => (
              <button
                key={type.value}
                onClick={() => setEventTypeFilter(type.value)}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  eventTypeFilter === type.value
                    ? 'bg-[#7B4BFF] text-white'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                {type.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <div className="text-sm text-gray-400 mb-2">Status:</div>
          <div className="flex space-x-2">
            {['all', 'queued', 'sent', 'acked', 'failed', 'dlq'].map((status) => (
              <button
                key={status}
                onClick={() => setStatusFilter(status)}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  statusFilter === status
                    ? 'bg-[#7B4BFF] text-white'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                {status === 'all' ? 'Wszystkie' : getStatusText(status.toUpperCase())}
              </button>
            ))}
          </div>
        </div>
      </div>

      {queueItems.length === 0 ? (
        <div className="bg-gray-800 rounded-lg p-12 text-center">
          <p className="text-gray-400">Brak elementów w kolejce</p>
        </div>
      ) : (
        <div className="space-y-4">
          {queueItems.map((item) => (
            <div
              key={item.id}
              className="bg-gray-800 rounded-lg p-6 border border-gray-700"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  {getStatusIcon(item.status)}
                  <div>
                    <div className="flex items-center space-x-2">
                      <span className="text-white font-medium">
                        {item.event_type === 'dm.inbound' ? 'Wiadomość' : 'Komentarz'}
                      </span>
                      <span className="px-2 py-1 text-xs rounded-full bg-gray-700 text-gray-300">
                        {getStatusText(item.status)}
                      </span>
                    </div>
                    <div className="text-sm text-gray-400 mt-1">
                      {new Date(item.created_at).toLocaleString('pl-PL')}
                    </div>
                  </div>
                </div>
                <div className="text-sm text-gray-400">
                  Próby: {item.tries}/{item.max_retries}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-gray-400 mb-1">Payload:</div>
                  <pre className="bg-gray-900 p-3 rounded text-xs text-gray-300 overflow-x-auto">
                    {JSON.stringify(item.payload, null, 2)}
                  </pre>
                </div>

                {item.response_payload && (
                  <div>
                    <div className="text-gray-400 mb-1">Odpowiedź:</div>
                    <pre className="bg-gray-900 p-3 rounded text-xs text-gray-300 overflow-x-auto">
                      {JSON.stringify(item.response_payload, null, 2)}
                    </pre>
                  </div>
                )}
              </div>

              {item.last_error && (
                <div className="mt-4 p-3 bg-red-900/20 border border-red-500/30 rounded">
                  <div className="text-red-400 text-sm font-medium mb-1">Błąd:</div>
                  <div className="text-red-300 text-xs">{item.last_error}</div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
